import React from "react";
import ModalForm from "@ui/Modal/Form";
import t from "../../../../../i18n";
import Text from "@ui/Text";
import Checkbox from "@ui/Checkbox";
import { getEventStatusText } from "../../utils/faultEventStatus";
import { css } from "@linaria/core";
import { spacings } from "@ui/Theme";
import { useGet } from "../../../../../api/useCall";
import useToasts from "@ui/Toast/useToasts";
import download from "downloadjs";
import { generateExportFileName } from "diagnostics-lib/string";
import type { Project } from "../../../../../api/useProjects";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import { translationLocale } from "../../../../../i18n/locale";

const ModalFormExportEvents = ({
  project,
  aggregationInterval,
  onRequestClose,
}: {
  project: Project;
  aggregationInterval: {
    from: Date;
    to: Date;
  };
  onRequestClose: () => void;
}) => {
  const { addToast } = useToasts();
  const exportFaultEvents = useGet("/projects/:projectId(uuid)/events/export");

  const [checkboxState, setCheckboxState] = React.useState({
    TO_BE_ANALYSED: true,
    ONGOING: true,
    ACTION_IDENTIFIED: true,
    CLOSED_TRUE_DETECTION: true,
    CLOSED_FALSE_DETECTION: true,
  });

  const handleCheckboxChange = React.useCallback((key: keyof typeof checkboxState) => {
    setCheckboxState(prevState => ({
      ...prevState,
      [key]: !prevState[key],
    }));
  }, []);

  const handleSubmit = React.useCallback(async () => {
    try {
      const exportedData = await exportFaultEvents({
        routeParams: {
          projectId: project.id,
        },
        body: {
          statuses: Object.entries(checkboxState)
            .filter(([, value]) => value)
            .map(([key]) => key) as [keyof typeof checkboxState],
          locale: translationLocale.toUpperCase() as Uppercase<typeof translationLocale>,
          aggregationPeriod: {
            startDate: aggregationInterval.from,
            endDate: aggregationInterval.to,
          },
        },
      });
      download(
        exportedData.body as unknown as Blob,
        generateExportFileName({
          filename: "fault_events",
          projectName: project.name,
          extension: "csv",
        }),
      );
    } catch (error) {
      addToast("error", t("errors.backend_error"));
    }
    return true;
  }, [addToast, exportFaultEvents, project.id, project.name, checkboxState, aggregationInterval]);

  const canSubmitForm = React.useMemo(
    () => Object.values(checkboxState).every(value => value === false) === false,
    [checkboxState],
  );

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      onSubmit={canSubmitForm ? handleSubmit : false}
      okButtonName={t("commons.message_card.export")}
    >
      <Text>{t("project.fault_events.modal_form.export.text")}</Text>

      <div className={checkboxWrapper}>
        <Checkbox
          checked={checkboxState.TO_BE_ANALYSED}
          onChange={() => handleCheckboxChange("TO_BE_ANALYSED")}
        >
          {getEventStatusText("TO_BE_ANALYSED")}
        </Checkbox>
        <Checkbox checked={checkboxState.ONGOING} onChange={() => handleCheckboxChange("ONGOING")}>
          {getEventStatusText("ONGOING")}
        </Checkbox>
        <Checkbox
          checked={checkboxState.ACTION_IDENTIFIED}
          onChange={() => handleCheckboxChange("ACTION_IDENTIFIED")}
        >
          {getEventStatusText("ACTION_IDENTIFIED")}
        </Checkbox>
        <Checkbox
          checked={checkboxState.CLOSED_TRUE_DETECTION}
          onChange={() => handleCheckboxChange("CLOSED_TRUE_DETECTION")}
        >
          {getEventStatusText("CLOSED") + " - " + t("project.fault_event.detection.true")}
        </Checkbox>
        <Checkbox
          checked={checkboxState.CLOSED_FALSE_DETECTION}
          onChange={() => handleCheckboxChange("CLOSED_FALSE_DETECTION")}
        >
          {getEventStatusText("CLOSED") + " - " + t("project.fault_event.detection.false")}
        </Checkbox>
      </div>

      {canSubmitForm === false && (
        <AdditionnalMessage
          status="ERROR"
          text={t("project.fault_events.export.message.no_checkbox_selected")}
        />
      )}
    </ModalForm>
  );
};

export default ModalFormExportEvents;

//#region CSS
const checkboxWrapper = css`
  & > *:not(:first-child) {
    margin-top: ${spacings.xs};
  }
`;
//#endregion CSS
